/**
 * WHENLY ADMIN APP
 *
 * External warehouse management app with Google authentication.
 * Users sign in with Google, then connect their Shopify stores.
 */

import React, { useState, useEffect, useRef, createContext, useContext, Component, ErrorInfo } from 'react';
import { Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { AppProvider } from '@shopify/polaris';
import enTranslations from '@shopify/polaris/locales/en.json';
import { resolveAuthSelection } from './auth/selection';
import '@shopify/polaris/build/esm/styles.css';

// Pages
import Dashboard from './pages/Dashboard';
import DeliveryRules from './pages/DeliveryRules';
import BlockedDates from './pages/BlockedDates';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import Shipments from './pages/Shipments';
import Fleet from './pages/Fleet';
import Couriers from './pages/Couriers';
import WarehousePartners from './pages/WarehousePartners';
import ShopifyPrivacy from './pages/ShopifyPrivacy';
import Warehouses from './pages/Warehouses';
import Inventory from './pages/Inventory';
import HeldGoods from './pages/HeldGoods';
import InventoryOperations from './pages/InventoryOperations';
import PickLists from './pages/PickLists';
import PickListDetail from './pages/PickListDetail';
import PackingLists from './pages/PackingLists';
import PackingListDetail from './pages/PackingListDetail';
import StockEnrollment from './pages/StockEnrollment';
import OpeningStock from './pages/OpeningStock';
import WarehouseReceiving from './pages/WarehouseReceiving';
import BinMoves from './pages/BinMoves';
import StockTakes from './pages/StockTakes';
import Settings from './pages/Settings';
import ShippingCalendar from './pages/ShippingCalendar';
import ShippingZones from './pages/ShippingZones';
import CustomiseStorefront from './pages/CustomiseStorefront';
import ProductSettings from './pages/ProductSettings';
import InitialSync from './pages/InitialSync';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import DeliveryZones from './pages/DeliveryZones';
import AlternativeProducts from './pages/AlternativeProducts';
import RoleBuilder from './pages/RoleBuilder';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import AcceptInvitation from './pages/AcceptInvitation';
import ConnectStore from './pages/ConnectStore';
import VendorRules from './pages/VendorRules';
import SyncSettings from './pages/SyncSettings';
import ShopifyUpdates from './pages/ShopifyUpdates';
import StockComparison from './pages/StockComparison';
import OrganizationSettings from './pages/OrganizationSettings';
import TeamMembers from './pages/TeamMembers';
import DeliveryCapacity from './pages/DeliveryCapacity';
import WeightRules from './pages/WeightRules';
import Returns from './pages/Returns';
import ReturnDetail from './pages/ReturnDetail';
import ReturnReasons from './pages/ReturnReasons';
import SupplierReturns from './pages/SupplierReturns';
import SupplierReturnSetup from './pages/SupplierReturnSetup';
import SupplierReturnDetail from './pages/SupplierReturnDetail';
import SupplierInvoices from './pages/SupplierInvoices';
import SupplierInvoiceDetail from './pages/SupplierInvoiceDetail';
import RoutesPage from './pages/Routes';
import RouteDetail from './pages/RouteDetail';
import RouteBuilder from './pages/RouteBuilder';
import Reports from './pages/Reports';
import BackorderReport from './pages/BackorderReport';
import ProductFlowReport from './pages/ProductFlowReport';
import BestSellersReport from './pages/BestSellersReport';
import SlowMovingReport from './pages/SlowMovingReport';
import PickerProductivityReport from './pages/PickerProductivityReport';
import StorageFees from './pages/StorageFees';
import Help from './pages/Help';
import PurchaseOrders from './pages/PurchaseOrders';
import PurchaseOrderDetail from './pages/PurchaseOrderDetail';
import Suppliers from './pages/Suppliers';
import SalesVelocityReport from './pages/SalesVelocityReport';
import ReorderRecommendations from './pages/ReorderRecommendations';
import IntrastatReport from './pages/IntrastatReport';
import StockValuation from './pages/StockValuation';
import ValuationSnapshots from './pages/ValuationSnapshots';
import ProfitAnalysis from './pages/ProfitAnalysis';

// New Phase 15 Features
import SlottingDashboard from './pages/SlottingDashboard';
import PutAwayTasks from './pages/PutAwayTasks';
import MergeRules from './pages/MergeRules';
import AutomationRules from './pages/AutomationRules';
import AutomationRuleEditor from './pages/AutomationRuleEditor';
import AutomationLogs from './pages/AutomationLogs';
import SerialNumbers from './pages/SerialNumbers';
import SerialNumberDetail from './pages/SerialNumberDetail';
import DamageReports from './pages/DamageReports';
import PurchaseDamageHolding from './pages/PurchaseDamageHolding';
import SupplierDamageHolding from './pages/SupplierDamageHolding';
import DamageReportDetail from './pages/DamageReportDetail';
import CreateDamageReport from './pages/CreateDamageReport';
import TransferOrders from './pages/TransferOrders';
import TransferOrderDetail from './pages/TransferOrderDetail';
import CreateTransferOrder from './pages/CreateTransferOrder';
import DeliveryCosts from './pages/DeliveryCosts';
import DeliveryCostReport from './pages/DeliveryCostReport';
import DeliveryPerformanceReport from './pages/DeliveryPerformanceReport';
import ErrorAnalytics from './pages/ErrorAnalytics';
import InternationalDocuments from './pages/InternationalDocuments';

// Admin pages
import AdminLayout from './admin/AdminLayout';
import AdminDashboard from './admin/AdminDashboard';
import CustomersList from './admin/CustomersList';
import CustomerDetail from './admin/CustomerDetail';
import UsersList from './admin/UsersList';
import Plans from './admin/Plans';
import Revenue from './admin/Revenue';
import SuperAdmins from './admin/SuperAdmins';
import DiscountCodes from './admin/DiscountCodes';
import { HeaderAlerts } from './components/HeaderAlerts';

// ============================================================
// STYLES
// ============================================================

const styles = `
  :root {
    --sidebar-bg: #4a9b8c;
    --sidebar-hover: #3d8577;
    --sidebar-active: #357569;
    --sidebar-text: #ffffff;
    --sidebar-text-muted: rgba(255, 255, 255, 0.7);
    --sidebar-width: 240px;
    --header-height: 56px;
    --content-bg: #f4f6f8;
    --card-bg: #ffffff;
    --text-primary: #1a1a1a;
    --text-secondary: #637381;
    --border-color: #e1e3e5;
  }

  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    background: var(--content-bg);
    color: var(--text-primary);
  }

  .app-layout {
    display: flex;
    min-height: 100vh;
  }

  /* Sidebar */
  .sidebar {
    width: var(--sidebar-width);
    background: var(--sidebar-bg);
    color: var(--sidebar-text);
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: 100;
    overflow-y: auto;
    transition: transform 0.3s ease;
  }

  .sidebar-header {
    padding: 16px 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  .sidebar-logo {
    font-size: 22px;
    font-weight: 600;
    color: var(--sidebar-text);
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .sidebar-logo span {
    font-weight: 300;
    opacity: 0.8;
  }

  .sidebar-nav {
    flex: 1;
    padding: 12px 0;
  }

  .nav-section {
    margin-bottom: 4px;
  }

  .nav-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 20px;
    cursor: pointer;
    transition: background 0.15s ease;
  }

  .nav-section-header:hover {
    background: var(--sidebar-hover);
  }

  .nav-section-title {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 14px;
    font-weight: 500;
  }

  .nav-section-title svg {
    width: 20px;
    height: 20px;
    opacity: 0.9;
  }

  .nav-section-header > svg {
    width: 14px;
    height: 14px;
    opacity: 0.5;
    transition: transform 0.2s ease;
    flex-shrink: 0;
  }

  .nav-section-header.open > svg {
    transform: rotate(90deg);
  }

  .nav-items {
    overflow: hidden;
    max-height: 0;
    transition: max-height 0.25s ease;
  }

  .nav-items.open {
    max-height: none;
  }

  .nav-item {
    display: flex;
    align-items: center;
    padding: 9px 20px 9px 52px;
    color: var(--sidebar-text-muted);
    text-decoration: none;
    font-size: 13px;
    transition: all 0.15s ease;
    cursor: pointer;
  }

  .nav-item:hover {
    background: var(--sidebar-hover);
    color: var(--sidebar-text);
  }

  .nav-item.active {
    background: var(--sidebar-active);
    color: var(--sidebar-text);
  }

  .nav-item::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    margin-right: 12px;
    opacity: 0.5;
  }

  .nav-item.active::before {
    opacity: 1;
  }

  /* Single nav item (no children) */
  .nav-single {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 20px;
    color: var(--sidebar-text);
    text-decoration: none;
    font-size: 14px;
    font-weight: 500;
    transition: background 0.15s ease;
  }

  .nav-single:hover {
    background: var(--sidebar-hover);
  }

  .nav-single.active {
    background: var(--sidebar-active);
  }

  .nav-single svg {
    width: 20px;
    height: 20px;
    opacity: 0.9;
  }

  /* Main content area */
  .main-wrapper {
    flex: 1;
    margin-left: var(--sidebar-width);
    display: flex;
    flex-direction: column;
  }

  /* Header */
  .header {
    height: var(--header-height);
    background: var(--card-bg);
    border-bottom: 1px solid var(--border-color);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 24px;
    position: sticky;
    top: 0;
    z-index: 50;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .mobile-menu-btn {
    display: none;
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    border-radius: 6px;
  }

  .mobile-menu-btn:hover {
    background: var(--content-bg);
  }

  .header-shop-name {
    font-size: 14px;
    color: var(--text-secondary);
    padding: 6px 12px;
    background: var(--content-bg);
    border-radius: 6px;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .header-date {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--text-secondary);
    padding: 6px 12px;
    background: var(--content-bg);
    border-radius: 6px;
  }

  .header-icons {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .header-icon-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    border-radius: 6px;
    color: var(--text-secondary);
    transition: all 0.15s ease;
  }

  .header-icon-btn:hover {
    background: var(--content-bg);
    color: var(--text-primary);
  }

  .header-icon-btn svg {
    width: 20px;
    height: 20px;
  }

  /* User menu */
  .user-menu {
    position: relative;
  }

  .user-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    cursor: pointer;
    border: 2px solid transparent;
    transition: border-color 0.15s ease;
  }

  .user-avatar:hover {
    border-color: var(--sidebar-bg);
  }

  .user-avatar-placeholder {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--sidebar-bg);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
  }

  .user-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    background: var(--card-bg);
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    min-width: 220px;
    z-index: 200;
    overflow: hidden;
  }

  .user-dropdown-header {
    padding: 16px;
    border-bottom: 1px solid var(--border-color);
  }

  .user-dropdown-name {
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 2px;
  }

  .user-dropdown-email {
    font-size: 12px;
    color: var(--text-secondary);
  }

  .user-dropdown-section {
    padding: 8px 0;
    border-bottom: 1px solid var(--border-color);
  }

  .user-dropdown-section:last-child {
    border-bottom: none;
  }

  .user-dropdown-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 16px;
    font-size: 13px;
    color: var(--text-primary);
    text-decoration: none;
    cursor: pointer;
    transition: background 0.15s ease;
  }

  .user-dropdown-item:hover {
    background: var(--content-bg);
  }

  .user-dropdown-item.active {
    background: rgba(74, 155, 140, 0.1);
    color: var(--sidebar-bg);
  }

  .user-dropdown-item svg {
    width: 16px;
    height: 16px;
    color: var(--text-secondary);
  }

  /* Content */
  .main-content {
    flex: 1;
    padding: 24px;
    background: var(--content-bg);
  }

  /* Loading */
  .loading-screen {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100vh;
    background: var(--content-bg);
  }

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--border-color);
    border-top-color: var(--sidebar-bg);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* Mobile */
  @media (max-width: 768px) {
    .sidebar {
      transform: translateX(-100%);
    }

    .sidebar.open {
      transform: translateX(0);
    }

    .sidebar-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      z-index: 99;
      opacity: 0;
      visibility: hidden;
      transition: all 0.3s ease;
    }

    .sidebar-overlay.open {
      opacity: 1;
      visibility: visible;
    }

    .main-wrapper {
      margin-left: 0;
    }

    .mobile-menu-btn {
      display: block;
    }

    .header-shop-name {
      display: none;
    }

    .main-content {
      padding: 16px;
    }
  }
`;

// ============================================================
// SVG ICONS
// ============================================================

const Icons = {
  Menu: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 12h18M3 6h18M3 18h18" />
    </svg>
  ),
  Home: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
      <polyline points="9,22 9,12 15,12 15,22" />
    </svg>
  ),
  Calendar: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  Settings: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  ),
  Orders: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10,9 9,9 8,9" />
    </svg>
  ),
  Warehouse: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 21h18" />
      <path d="M5 21V7l7-4 7 4v14" />
      <path d="M9 21v-6h6v6" />
      <path d="M10 9h4" />
    </svg>
  ),
  Delivery: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="1" y="3" width="15" height="13" rx="1" />
      <path d="M16 8h4l3 3v5h-7V8z" />
      <circle cx="5.5" cy="18.5" r="2.5" />
      <circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  ),
  Returns: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="1,4 1,10 7,10" />
      <path d="M3.51 15a9 9 0 102.13-9.36L1 10" />
    </svg>
  ),
  Reports: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  ),
  Bell: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 01-3.46 0" />
    </svg>
  ),
  Store: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
      <polyline points="9,22 9,12 15,12 15,22" />
    </svg>
  ),
  Logout: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
      <polyline points="16,17 21,12 16,7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  ),
  Check: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="20,6 9,17 4,12" />
    </svg>
  ),
  Help: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
};

// ============================================================
// ERROR BOUNDARY
// ============================================================

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<{ children: React.ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('React Error Boundary caught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '40px', fontFamily: 'sans-serif', maxWidth: '800px', margin: '0 auto' }}>
          <h1 style={{ color: '#d32f2f' }}>Something went wrong</h1>
          <p>The application encountered an error. Please try refreshing the page.</p>
          <details style={{ marginTop: '20px', background: '#f5f5f5', padding: '16px', borderRadius: '4px' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Error Details</summary>
            <pre style={{ overflow: 'auto', marginTop: '10px', fontSize: '12px' }}>
              {this.state.error?.toString()}
              {'\n\n'}
              {this.state.error?.stack}
            </pre>
          </details>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              background: '#4a9b8c',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Refresh Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// ============================================================
// AUTH CONTEXT
// ============================================================

interface User {
  id: string;
  email: string;
  name: string;
  picture?: string;
  hasPassword?: boolean;
  emailVerified?: boolean;
  googleId?: string;
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  role: string; // owner, admin, member
  authMode: 'native' | 'google' | 'both';
  googleOAuthEnabled: boolean;
}

interface Shop {
  id: string;
  domain: string;
  name: string;
  role: string;
  organizationId?: string;
}

interface AuthContextType {
  // User state
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  authMethod: 'native' | 'google' | null;
  hasPassword: boolean;
  isEmailVerified: boolean;

  // Organizations
  organizations: Organization[];
  currentOrganization: Organization | null;
  setCurrentOrganization: (org: Organization) => void;

  // Shops
  shops: Shop[];
  currentShop: Shop | null;
  setCurrentShop: (shop: Shop) => void;

  // Permissions
  permissions: string[];
  hasPermission: (permission: string) => boolean;

  // Actions
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

const API_URL = import.meta.env.VITE_API_URL || 'https://api.whenlyapp.com';

function getShopFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('shop');
}

/**
 * Extract organization slug from subdomain.
 * e.g., acme.whenlyapp.com → 'acme'
 * Returns null if on www, app, api, or root domain.
 */
function getOrgSlugFromSubdomain(): string | null {
  const hostname = window.location.hostname;

  // Skip for localhost development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return null;
  }

  // Parse the hostname to extract subdomain
  const parts = hostname.split('.');

  // Need at least 3 parts for a subdomain (e.g., acme.whenlyapp.com)
  if (parts.length >= 3) {
    const subdomain = parts[0];
    // Skip reserved subdomains
    if (!['www', 'app', 'api'].includes(subdomain)) {
      return subdomain;
    }
  }

  return null;
}

// ============================================================
// AUTH PROVIDER
// ============================================================

function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [authMethod, setAuthMethod] = useState<'native' | 'google' | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrganization, setCurrentOrgState] = useState<Organization | null>(null);
  const [shops, setShops] = useState<Shop[]>([]);
  const [currentShop, setCurrentShopState] = useState<Shop | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const authRequest = useRef(0);

  /**
   * Check if user has a specific permission.
   * Supports wildcard '*' for admin access.
   */
  const hasPermission = (permission: string): boolean => {
    if (!permissions || permissions.length === 0) return false;
    // Admin has all permissions
    if (permissions.includes('*')) return true;
    // Check for exact match or wildcard prefix match (e.g., 'orders.*' matches 'orders.view')
    return permissions.some(p => {
      if (p === permission) return true;
      // Support wildcard suffix (e.g., 'orders.*' matches 'orders.view')
      if (p.endsWith('.*')) {
        const prefix = p.slice(0, -2);
        return permission.startsWith(prefix + '.');
      }
      return false;
    });
  };

  const refreshAuth = async () => {
    const request = ++authRequest.current;
    const current = () => authRequest.current === request;
    const preference = { organizationId: localStorage.getItem('currentOrgId'), organizationSlug: getOrgSlugFromSubdomain(), shopId: localStorage.getItem('currentShopId'), shopDomain: getShopFromUrl() };
    const apply = async (initial: Parameters<typeof updateStateFromResponse>[0]) => {
      const selected = await resolveAuthSelection(initial, preference, async headers => {
        const response = await fetch(`${API_URL}/api/auth/me`, { credentials: 'include', headers });
        if (!response.ok) throw new Error('Could not load the selected company and warehouse.');
        return response.json();
      });
      if (!current()) return;
      if (selected.organizationId) localStorage.setItem('currentOrgId', selected.organizationId);
      else localStorage.removeItem('currentOrgId');
      if (selected.shopId) localStorage.setItem('currentShopId', selected.shopId);
      else localStorage.removeItem('currentShopId');
      updateStateFromResponse(selected.data);
    };
    try {
      if (!document.cookie.includes('logged_in=true')) {
        resetState();
        setIsLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include',
      });

      if (!response.ok) {
        const refreshResponse = await fetch(`${API_URL}/api/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });

        if (!refreshResponse.ok) {
          if (!current()) return;
          resetState();
          setIsLoading(false);
          return;
        }

        const retryResponse = await fetch(`${API_URL}/api/auth/me`, {
          credentials: 'include',
        });

        if (!retryResponse.ok) {
          if (!current()) return;
          resetState();
          setIsLoading(false);
          return;
        }

        const data = await retryResponse.json();
        await apply(data);
      } else {
        const data = await response.json();
        await apply(data);
      }
    } catch (err) {
      if (!current()) return;
      console.error('Auth check failed:', err);
      resetState();
    } finally {
      if (current()) setIsLoading(false);
    }
  };

  const resetState = () => {
    setUser(null);
    setAuthMethod(null);
    setOrganizations([]);
    setCurrentOrgState(null);
    setShops([]);
    setCurrentShopState(null);
    setPermissions([]);
  };

  const updateStateFromResponse = (data: {
    user: User;
    authMethod?: 'native' | 'google';
    organizations?: Organization[];
    shops?: Shop[];
    permissions?: string[];
    currentOrganization?: { id: string } | null;
    currentShop?: { id: string } | null;
  }) => {
    setUser(data.user);
    setAuthMethod(data.authMethod || null);
    setOrganizations(data.organizations || []);
    setShops(data.shops || []);
    setPermissions(data.permissions || []);
    selectCurrentOrganization(data.organizations || []);
    selectCurrentShop(data.shops || []);
  };

  const selectCurrentOrganization = (availableOrgs: Organization[]) => {
    if (!availableOrgs || availableOrgs.length === 0) {
      setCurrentOrgState(null);
      return;
    }

    // Priority 1: Check subdomain for organization slug
    const subdomainSlug = getOrgSlugFromSubdomain();
    if (subdomainSlug) {
      const subdomainOrg = availableOrgs.find((o) => o.slug === subdomainSlug);
      if (subdomainOrg) {
        setCurrentOrgState(subdomainOrg);
        localStorage.setItem('currentOrgId', subdomainOrg.id);
        return;
      }
      // User is on a subdomain they don't have access to - will be handled by redirect
      console.warn(`User does not have access to organization: ${subdomainSlug}`);
    }

    // Priority 2: Use saved organization from localStorage
    const savedOrgId = localStorage.getItem('currentOrgId');
    const savedOrg = availableOrgs.find((o) => o.id === savedOrgId);

    // Priority 3: Default to first available organization
    setCurrentOrgState(savedOrg || availableOrgs[0] || null);
  };

  const selectCurrentShop = (availableShops: Shop[]) => {
    if (!availableShops || availableShops.length === 0) {
      setCurrentShopState(null);
      return;
    }

    const shopFromUrl = getShopFromUrl();
    if (shopFromUrl) {
      const matchingShop = availableShops.find((s) => s.domain === shopFromUrl);
      if (matchingShop) {
        setCurrentShopState(matchingShop);
        localStorage.setItem('currentShopId', matchingShop.id);
        const url = new URL(window.location.href);
        url.searchParams.delete('shop');
        window.history.replaceState({}, '', url.pathname + url.search);
        return;
      }
    }

    const savedShopId = localStorage.getItem('currentShopId');
    const savedShop = availableShops.find((s) => s.id === savedShopId);
    setCurrentShopState(savedShop || availableShops[0] || null);
  };

  const setCurrentOrganization = (org: Organization) => {
    setCurrentOrgState(org);
    localStorage.setItem('currentOrgId', org.id);

    // The server returns shops and permissions for one company at a time.
    // Clear the previous context before loading the newly selected company.
    setCurrentShopState(null);
    setShops([]);
    setPermissions([]);
    setIsLoading(true);
    localStorage.removeItem('currentShopId');
    void refreshAuth();

    // If we're on a subdomain that doesn't match the new org, redirect
    const currentSubdomain = getOrgSlugFromSubdomain();
    if (currentSubdomain && currentSubdomain !== org.slug) {
      // Redirect to the correct org subdomain
      const protocol = window.location.protocol;
      const baseDomain = window.location.hostname.split('.').slice(-2).join('.');
      window.location.href = `${protocol}//${org.slug}.${baseDomain}${window.location.pathname}`;
    }
  };

  const setCurrentShop = (shop: Shop) => {
    if (currentOrganization && shop.organizationId && shop.organizationId !== currentOrganization.id) return;
    setCurrentShopState(null);
    setPermissions([]);
    setIsLoading(true);
    localStorage.setItem('currentShopId', shop.id);
    void refreshAuth();
  };

  const logout = async () => {
    authRequest.current++;
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      console.error('Logout failed:', err);
    } finally {
      resetState();
      localStorage.removeItem('currentShopId');
      localStorage.removeItem('currentOrgId');
    }
  };

  useEffect(() => {
    refreshAuth();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        authMethod,
        hasPassword: !!user?.hasPassword,
        isEmailVerified: !!user?.emailVerified,
        organizations,
        currentOrganization,
        setCurrentOrganization,
        shops,
        currentShop,
        setCurrentShop,
        permissions,
        hasPermission,
        logout,
        refreshAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ============================================================
// PROTECTED ROUTE
// ============================================================

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// ============================================================
// REQUIRE SHOP
// ============================================================

function RequireShop({ children }: { children: React.ReactNode }) {
  const { currentShop, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
      </div>
    );
  }

  if (!currentShop) {
    return <Navigate to="/connect" replace />;
  }

  return <>{children}</>;
}

// ============================================================
// NAVIGATION CONFIG WITH PERMISSIONS
// ============================================================

interface NavItem {
  label: string;
  path: string;
  permission: string; // Required permission code
}

interface NavSection {
  title: string;
  icon: React.FC;
  items?: NavItem[];
  path?: string;
  permission?: string; // Permission for single-item sections
}

const navigationConfig: NavSection[] = [
  {
    title: 'Dashboard',
    icon: Icons.Home,
    path: '/',
    permission: 'dashboard.stats.view',
  },
  {
    title: 'Calendar',
    icon: Icons.Calendar,
    items: [
      { label: 'Shipping Calendar', path: '/calendar', permission: 'calendar.view' },
      { label: 'Delivery Rules', path: '/delivery-rules', permission: 'rules.list' },
      { label: 'Blocked Dates', path: '/blocked-dates', permission: 'rules.blocked_dates.manage' },
    ],
  },
  {
    title: 'Configuration',
    icon: Icons.Settings,
    items: [
      { label: 'Customise Storefront', path: '/customise-storefront', permission: 'settings.storefront.view' },
      { label: 'Delivery Zones', path: '/delivery-zones', permission: 'zones.list' },
      { label: 'Delivery Capacity', path: '/delivery-capacity', permission: 'delivery.view' },
      { label: 'Weight Rules', path: '/weight-rules', permission: 'delivery.view' },
      { label: 'Shipping Settings', path: '/shipping-settings', permission: 'shipping.zones.view' },
      { label: 'Product Settings', path: '/product-settings', permission: 'products.view' },
    ],
  },
  {
    title: 'Orders',
    icon: Icons.Orders,
    items: [
      { label: 'All Orders', path: '/orders', permission: 'orders.list' },
      { label: 'Shipments', path: '/shipments', permission: 'orders.view' },
      { label: 'Merge Rules', path: '/merge-rules', permission: 'orders.view' },
      { label: 'Automation', path: '/automation', permission: 'settings.view' },
    ],
  },
  {
    title: 'Delivery Routes',
    icon: Icons.Delivery,
    items: [
      { label: 'Routes', path: '/routes', permission: 'routes.list' },
      { label: 'Fleet', path: '/fleet', permission: 'routes.view' },
      { label: 'Build Route', path: '/routes/build', permission: 'routes.create' },
    ],
  },
  {
    title: 'Returns',
    icon: Icons.Returns,
    items: [
      { label: 'Customer Returns', path: '/returns', permission: 'returns.list' },
      { label: 'Return Reasons', path: '/returns/reasons', permission: 'returns.list' },
    ],
  },
  {
    title: 'Supplier Returns',
    icon: Icons.Returns,
    items: [
      { label: 'Supplier Returns', path: '/supplier-returns', permission: 'supplier_returns.list' },
      { label: 'Supplier Invoices', path: '/supplier-invoices', permission: 'supplier_invoices.list' },
    ],
  },
  {
    title: 'Purchase Orders',
    icon: Icons.Orders,
    items: [
      { label: 'Purchase Orders', path: '/purchase-orders', permission: 'inventory.view' },
      { label: 'Suppliers', path: '/suppliers', permission: 'inventory.view' },
    ],
  },
  {
    title: 'Warehouse',
    icon: Icons.Warehouse,
    items: [
      { label: 'Products', path: '/products', permission: 'products.list' },
      { label: 'Vendor Rules', path: '/products/vendor-rules', permission: 'products.vendors.list' },
      { label: 'Alternative Products', path: '/alternative-products', permission: 'warehouse.alternatives.list' },
      { label: 'Warehouses', path: '/warehouses', permission: 'warehouses.list' },
      { label: 'Inventory', path: '/inventory', permission: 'inventory.list' },
      { label: 'Held goods', path: '/held-goods', permission: 'inventory.view' },
      { label: 'Pick Lists', path: '/pick-lists', permission: 'picking.lists.list' },
      { label: 'Packing Lists', path: '/packing-lists', permission: 'warehouse.packing_lists.list' },
      { label: 'Stock Takes', path: '/stock-takes', permission: 'stock_takes.list' },
      { label: 'Stock tracking setup', path: '/stock-enrollment', permission: 'inventory.manage' },
      { label: 'Opening stock', path: '/opening-stock', permission: 'inventory.adjust' },
      { label: 'Receive deliveries', path: '/receiving', permission: 'inventory.receive' },
      { label: 'Move stock between bins', path: '/bin-moves', permission: 'inventory.transfer' },
      { label: 'Dynamic Slotting', path: '/slotting', permission: 'inventory.view' },
      { label: 'Put-Away Tasks', path: '/put-away', permission: 'inventory.view' },
      { label: 'Serial Numbers', path: '/serial-numbers', permission: 'inventory.view' },
      { label: 'Damage Reports', path: '/damage-reports', permission: 'inventory.view' },
      { label: 'Transfer Orders', path: '/transfer-orders', permission: 'inventory.view' },
    ],
  },
  {
    title: 'Reports',
    icon: Icons.Reports,
    items: [
      { label: 'Reports', path: '/reports', permission: 'reports.view' },
      { label: 'Sales Velocity', path: '/reports/sales-velocity', permission: 'reports.view' },
      { label: 'Reorder Recommendations', path: '/reports/reorder', permission: 'reports.view' },
      { label: 'Best Sellers', path: '/reports/best-sellers', permission: 'reports.view' },
      { label: 'Slow Moving Stock', path: '/reports/slow-moving', permission: 'reports.view' },
      { label: 'Picker Productivity', path: '/reports/picker-productivity', permission: 'reports.view' },
      { label: 'Backorder Report', path: '/reports/backorder', permission: 'reports.backorder' },
      { label: 'Product Flow', path: '/reports/product-flow', permission: 'reports.product_flow' },
      { label: 'Intrastat (EU)', path: '/reports/intrastat', permission: 'reports.view' },
      { label: 'Stock Valuation', path: '/reports/stock-valuation', permission: 'reports.view' },
      { label: 'Profit Analysis', path: '/reports/profit-analysis', permission: 'reports.view' },
      { label: 'Delivery Costs', path: '/delivery-costs', permission: 'reports.view' },
      { label: 'Error Analytics', path: '/error-analytics', permission: 'reports.view' },
      { label: 'International Docs', path: '/international-documents', permission: 'orders.view' },
    ],
  },
  {
    title: 'Settings',
    icon: Icons.Settings,
    items: [
      { label: 'Data Import', path: '/initial-sync', permission: 'sync.status.view' },
      { label: 'Shopify updates', path: '/shopify-updates', permission: 'sync.status.view' },
      { label: 'Sync Settings', path: '/sync-settings', permission: 'sync.status.view' },
      { label: 'Couriers', path: '/couriers', permission: 'settings.view' },
      { label: 'Warehouse partners', path: '/warehouse-partners', permission: 'settings.manage' },
      { label: 'Shopify privacy', path: '/shopify-privacy', permission: 'settings.view' },
      { label: 'Storage Fees', path: '/storage-fees', permission: 'settings.view' },
      { label: 'Roles & Permissions', path: '/roles', permission: 'settings.roles' },
      { label: 'Settings', path: '/settings', permission: 'settings.view' },
    ],
  },
  {
    title: 'Help',
    icon: Icons.Help,
    path: '/help',
    permission: 'help.view',
  },
];

// ============================================================
// SIDEBAR COMPONENT
// ============================================================

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

// Supreme Leader email (platform owner)
const SUPREME_LEADER_EMAIL = 'dthake@ms.vanilla.net.mt';

function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const isSupremeLeader = user?.email === SUPREME_LEADER_EMAIL;

  // Filter navigation based on permissions
  // Filter sections and items that the user has permission to view
  const filteredNavigationConfig = navigationConfig
    .map(section => {
      // For sections with items, filter the items by permission
      if (section.items) {
        const filteredItems = section.items.filter(item => hasPermission(item.permission));
        // Only include section if it has visible items
        if (filteredItems.length === 0) return null;
        return { ...section, items: filteredItems };
      }
      // For single-item sections, check section permission
      if (section.permission && !hasPermission(section.permission)) {
        return null;
      }
      return section;
    })
    .filter((section): section is NavSection => section !== null);

  const [openSections, setOpenSections] = useState<Set<string>>(() => {
    // Open sections that contain the current path
    const initial = new Set<string>();
    filteredNavigationConfig.forEach(section => {
      if (section.items?.some(item => location.pathname === item.path || location.pathname.startsWith(item.path + '/'))) {
        initial.add(section.title);
      }
    });
    return initial;
  });

  const toggleSection = (title: string) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(title)) {
        next.delete(title);
      } else {
        next.add(title);
      }
      return next;
    });
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  // Highlight the closest visible destination, keeping detail pages under their parent.
  const activePath = filteredNavigationConfig
    .flatMap(section => section.items?.map(item => item.path) ?? (section.path ? [section.path] : []))
    .filter(path => location.pathname === path || (path !== '/' && location.pathname.startsWith(path + '/')))
    .sort((a, b) => b.length - a.length)[0];
  const isActive = (path: string) => path === activePath;

  return (
    <>
      <div className={`sidebar-overlay ${isOpen ? 'open' : ''}`} onClick={onClose} />
      <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <a href="/" className="sidebar-logo" onClick={(e) => { e.preventDefault(); handleNavigation('/'); }}>
            whenly<span>app</span>
          </a>
        </div>

        <nav className="sidebar-nav">
          {filteredNavigationConfig.map((section) => {
            const Icon = section.icon;
            const isSectionOpen = openSections.has(section.title);
            const hasItems = section.items && section.items.length > 0;

            if (!hasItems && section.path) {
              return (
                <a
                  key={section.title}
                  className={`nav-single ${isActive(section.path) ? 'active' : ''}`}
                  onClick={(e) => { e.preventDefault(); handleNavigation(section.path!); }}
                  href={section.path}
                >
                  <Icon />
                  {section.title}
                </a>
              );
            }

            return (
              <div key={section.title} className="nav-section">
                <div
                  className={`nav-section-header ${isSectionOpen ? 'open' : ''}`}
                  onClick={() => toggleSection(section.title)}
                >
                  <span className="nav-section-title">
                    <Icon />
                    {section.title}
                  </span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9,6 15,12 9,18" />
                  </svg>
                </div>
                <div className={`nav-items ${isSectionOpen ? 'open' : ''}`}>
                  {section.items?.map((item) => (
                    <a
                      key={item.path}
                      className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                      onClick={(e) => { e.preventDefault(); handleNavigation(item.path); }}
                      href={item.path}
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Supreme Leader link - only visible to platform owner */}
          {isSupremeLeader && (
            <div className="nav-section" style={{ marginTop: 'auto', borderTop: '1px solid var(--border-color)', paddingTop: '12px' }}>
              <a
                className={`nav-single ${location.pathname.startsWith('/admin') ? 'active' : ''}`}
                onClick={(e) => { e.preventDefault(); handleNavigation('/admin'); }}
                href="/admin"
                style={{ color: '#d4af37', fontWeight: 600 }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '20px', height: '20px' }}>
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                </svg>
                Supreme Leader
              </a>
            </div>
          )}
        </nav>
      </aside>
    </>
  );
}

// ============================================================
// HEADER COMPONENT
// ============================================================

interface HeaderProps {
  onMenuToggle: () => void;
}

function Header({ onMenuToggle }: HeaderProps) {
  const navigate = useNavigate();
  const { user, organizations, currentOrganization, setCurrentOrganization, shops, currentShop, setCurrentShop, logout } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userMenuButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!userMenuOpen) return;
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !userMenuRef.current?.contains(event.target)) setUserMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setUserMenuOpen(false); userMenuButtonRef.current?.focus(); }
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [userMenuOpen]);

  const today = new Date().toLocaleDateString('en-GB', {
    month: 'short',
    day: 'numeric',
  });

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <header className="header">
      <div className="header-left">
        <button className="mobile-menu-btn" onClick={onMenuToggle}>
          <Icons.Menu />
        </button>
        {currentOrganization && (
          <span className="header-shop-name">
            {currentOrganization.name}
            {currentShop && ` · ${currentShop.name || currentShop.domain}`}
          </span>
        )}
      </div>

      <div className="header-right">
        <span className="header-date">
          Today: {today}
        </span>

        <div className="header-icons">
          <button className="header-icon-btn" onClick={() => navigate('/')}>
            <Icons.Home />
          </button>
          <HeaderAlerts />
          <button className="header-icon-btn" onClick={() => navigate('/settings')}>
            <Icons.Settings />
          </button>
        </div>

        <div className="user-menu" ref={userMenuRef}>
          <button type="button" ref={userMenuButtonRef} aria-label="Your account" aria-expanded={userMenuOpen}
            aria-controls="account-dropdown" onClick={() => setUserMenuOpen(open => !open)}
            style={{ border: 0, padding: 0, background: 'transparent', borderRadius: '50%', cursor: 'pointer' }}>
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              className="user-avatar"
            />
          ) : (
            <span className="user-avatar-placeholder">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </span>
          )}

          </button>
          {userMenuOpen && (
            <div className="user-dropdown" id="account-dropdown">
              <div className="user-dropdown-header">
                <div className="user-dropdown-name">{user?.name}</div>
                <div className="user-dropdown-email">{user?.email}</div>
              </div>

              {organizations.length > 1 && (
                <div className="user-dropdown-section">
                  <div style={{ padding: '4px 16px', fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600 }}>
                    Organizations
                  </div>
                  {organizations.map(org => (
                    <div
                      key={org.id}
                      className={`user-dropdown-item ${org.id === currentOrganization?.id ? 'active' : ''}`}
                      onClick={() => { setCurrentOrganization(org); setUserMenuOpen(false); }}
                    >
                      <Icons.Store />
                      {org.name}
                      {org.id === currentOrganization?.id && <Icons.Check />}
                    </div>
                  ))}
                </div>
              )}

              {shops.length > 1 && (
                <div className="user-dropdown-section">
                  <div style={{ padding: '4px 16px', fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', fontWeight: 600 }}>
                    Stores
                  </div>
                  {shops.filter(s => !currentOrganization || s.organizationId === currentOrganization.id).map(shop => (
                    <div
                      key={shop.id}
                      className={`user-dropdown-item ${shop.id === currentShop?.id ? 'active' : ''}`}
                      onClick={() => { setCurrentShop(shop); setUserMenuOpen(false); }}
                    >
                      <Icons.Store />
                      {shop.name || shop.domain}
                      {shop.id === currentShop?.id && <Icons.Check />}
                    </div>
                  ))}
                </div>
              )}

              <div className="user-dropdown-section">
                <a className="user-dropdown-item" href="/connect" onClick={(e) => { e.preventDefault(); navigate('/connect'); setUserMenuOpen(false); }}>
                  <Icons.Store />
                  Connect Store
                </a>
              </div>

              <div className="user-dropdown-section">
                <div className="user-dropdown-item" onClick={handleLogout}>
                  <Icons.Logout />
                  Sign Out
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

// ============================================================
// APP CONTENT (Authenticated)
// ============================================================

function AppContent() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="app-layout">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="main-wrapper">
        <Header onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
        <main className="main-content">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/calendar" element={<ShippingCalendar />} />
            <Route path="/delivery-rules" element={<DeliveryRules />} />
            <Route path="/blocked-dates" element={<BlockedDates />} />
            <Route path="/customise-storefront" element={<CustomiseStorefront />} />
            <Route path="/delivery-zones" element={<DeliveryZones />} />
            <Route path="/delivery-capacity" element={<DeliveryCapacity />} />
            <Route path="/weight-rules" element={<WeightRules />} />
            <Route path="/shipping-settings" element={<ShippingZones />} />
            <Route path="/product-settings" element={<ProductSettings />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/orders/:orderId" element={<OrderDetail />} />
            <Route path="/orders/:orderId/shipments" element={<Shipments />} />
            <Route path="/shipments" element={<Shipments />} />
            <Route path="/fleet" element={<Fleet />} />
            <Route path="/couriers" element={<Couriers />} />
            <Route path="/warehouse-partners" element={<WarehousePartners />} />
            <Route path="/shopify-privacy" element={<ShopifyPrivacy />} />
            <Route path="/returns" element={<Returns />} />
            <Route path="/returns/reasons" element={<ReturnReasons />} />
            <Route path="/returns/:returnId" element={<ReturnDetail />} />
            <Route path="/supplier-returns" element={<SupplierReturns />} />
            <Route path="/supplier-return-setup" element={<SupplierReturnSetup />} />
            <Route path="/supplier-returns/:returnId" element={<SupplierReturnDetail />} />
            <Route path="/supplier-invoices" element={<SupplierInvoices />} />
            <Route path="/supplier-invoices/:invoiceId" element={<SupplierInvoiceDetail />} />
            <Route path="/purchase-orders" element={<PurchaseOrders />} />
            <Route path="/purchase-orders/:poId" element={<PurchaseOrderDetail />} />
            <Route path="/suppliers" element={<Suppliers />} />
            <Route path="/routes" element={<RoutesPage />} />
            <Route path="/routes/build" element={<RouteBuilder />} />
            <Route path="/routes/:routeId" element={<RouteDetail />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/reports/backorder" element={<BackorderReport />} />
            <Route path="/reports/product-flow" element={<ProductFlowReport />} />
            <Route path="/reports/best-sellers" element={<BestSellersReport />} />
            <Route path="/reports/slow-moving" element={<SlowMovingReport />} />
            <Route path="/reports/picker-productivity" element={<PickerProductivityReport />} />
            <Route path="/reports/sales-velocity" element={<SalesVelocityReport />} />
            <Route path="/reports/reorder" element={<ReorderRecommendations />} />
            <Route path="/reports/intrastat" element={<IntrastatReport />} />
            <Route path="/reports/stock-valuation" element={<StockValuation />} />
            <Route path="/reports/valuation-snapshots" element={<ValuationSnapshots />} />
            <Route path="/reports/profit-analysis" element={<ProfitAnalysis />} />
            <Route path="/products" element={<Products />} />
            <Route path="/products/vendor-rules" element={<VendorRules />} />
            <Route path="/products/:productId" element={<ProductDetail />} />
            <Route path="/alternative-products" element={<AlternativeProducts />} />
            <Route path="/warehouses" element={<Warehouses />} />
            <Route path="/inventory" element={<Inventory />} />
            <Route path="/held-goods" element={<HeldGoods />} />
            <Route path="/inventory-operations" element={<InventoryOperations />} />
            <Route path="/pick-lists" element={<PickLists />} />
            <Route path="/pick-lists/:pickListId" element={<PickListDetail />} />
            <Route path="/packing-lists" element={<PackingLists />} />
            <Route path="/packing-lists/:packingListId" element={<PackingListDetail />} />
            <Route path="/stock-enrollment/:productId?" element={<StockEnrollment />} />
            <Route path="/opening-stock/:productId?" element={<OpeningStock />} />
            <Route path="/receiving/:receiptId?" element={<WarehouseReceiving />} />
            <Route path="/bin-moves/:moveId?" element={<BinMoves />} />
            <Route path="/stock-takes" element={<StockTakes />} />
            {/* New Phase 15 Feature Routes */}
            <Route path="/slotting" element={<SlottingDashboard />} />
            <Route path="/put-away" element={<PutAwayTasks />} />
            <Route path="/merge-rules" element={<MergeRules />} />
            <Route path="/automation" element={<AutomationRules />} />
            <Route path="/automation/new" element={<AutomationRuleEditor />} />
            <Route path="/automation/:ruleId" element={<AutomationRuleEditor />} />
            <Route path="/automation/logs" element={<AutomationLogs />} />
            <Route path="/serial-numbers" element={<SerialNumbers />} />
            <Route path="/serial-numbers/:serial" element={<SerialNumberDetail />} />
            <Route path="/purchase-holdings/:holdId" element={<PurchaseDamageHolding />} />
            <Route path="/supplier-rejected/:itemId" element={<SupplierDamageHolding />} />
            <Route path="/damage-reports" element={<DamageReports />} />
            <Route path="/damage-reports/new" element={<CreateDamageReport />} />
            <Route path="/damage-reports/:reportId" element={<DamageReportDetail />} />
            <Route path="/transfer-orders" element={<TransferOrders />} />
            <Route path="/transfer-orders/new" element={<CreateTransferOrder />} />
            <Route path="/transfer-orders/:transferId" element={<TransferOrderDetail />} />
            <Route path="/delivery-costs" element={<DeliveryCosts />} />
            <Route path="/reports/delivery-costs" element={<DeliveryCostReport />} />
            <Route path="/reports/delivery-performance" element={<DeliveryPerformanceReport />} />
            <Route path="/error-analytics" element={<ErrorAnalytics />} />
            <Route path="/international-documents" element={<InternationalDocuments />} />
            <Route path="/initial-sync" element={<InitialSync />} />
            <Route path="/sync-settings" element={<SyncSettings />} />
            <Route path="/shopify-updates" element={<ShopifyUpdates />} />
            <Route path="/stock-comparison" element={<StockComparison />} />
            <Route path="/storage-fees" element={<StorageFees />} />
            <Route path="/roles" element={<RoleBuilder />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/help" element={<Help />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

// ============================================================
// MAIN APP
// ============================================================

export default function App() {
  return (
    <ErrorBoundary>
      <style>{styles}</style>
      <AppProvider i18n={enTranslations}>
        <AuthProvider>
          <Routes>
            {/* Public routes - Auth pages */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token?" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/invite/:token" element={<AcceptInvitation />} />
            <Route path="/invite" element={<AcceptInvitation />} />

            {/* Connect store (requires auth but not shop) */}
            <Route
              path="/connect"
              element={
                <ProtectedRoute>
                  <ConnectStore />
                </ProtectedRoute>
              }
            />

            {/* Organization settings (requires auth but not shop) */}
            <Route
              path="/settings/organization"
              element={
                <ProtectedRoute>
                  <OrganizationSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/organization/team"
              element={
                <ProtectedRoute>
                  <TeamMembers />
                </ProtectedRoute>
              }
            />

            {/* Super Admin routes (requires auth, NOT shop) */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="customers" element={<CustomersList />} />
              <Route path="customers/:id" element={<CustomerDetail />} />
              <Route path="users" element={<UsersList />} />
              <Route path="plans" element={<Plans />} />
              <Route path="discount-codes" element={<DiscountCodes />} />
              <Route path="revenue" element={<Revenue />} />
              <Route path="admins" element={<SuperAdmins />} />
            </Route>

            {/* Protected routes (require auth and shop) */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <RequireShop>
                    <AppContent />
                  </RequireShop>
                </ProtectedRoute>
              }
            />
          </Routes>
        </AuthProvider>
      </AppProvider>
    </ErrorBoundary>
  );
}
